/* XI Heritage theme scripts. Everything here enhances plain HTML forms and links,
   so the store still works if this file fails to load. */
(() => {
  const strings = (window.XI && window.XI.strings) || {};
  const routes = (window.XI && window.XI.routes) || {};

  /* ---------- Mobile drawer ---------- */
  document.addEventListener('click', (event) => {
    const closer = event.target.closest('[data-drawer-close]');
    if (closer) closer.closest('details').removeAttribute('open');
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('details[open]').forEach((el) => {
      if (el.matches('[data-drawer], .dropdown')) {
        el.removeAttribute('open');
        el.querySelector('summary')?.focus();
      }
    });
  });
  // Close desktop dropdowns when clicking elsewhere.
  document.addEventListener('click', (event) => {
    document.querySelectorAll('.dropdown[open]').forEach((el) => {
      if (!el.contains(event.target)) el.removeAttribute('open');
    });
  });

  /* ---------- Sort select auto-submit ---------- */
  document.querySelectorAll('[data-auto-submit]').forEach((select) => {
    select.addEventListener('change', () => select.form.submit());
  });

  /* ---------- Cart count + toast ---------- */
  const toast = document.getElementById('CartToast');
  let toastTimer;
  const showToast = (message) => {
    if (!toast) return;
    toast.querySelector('.toast__text').textContent = message;
    toast.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { toast.hidden = true; }, 4000);
  };
  const updateCartCount = async () => {
    try {
      const res = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.classList.toggle('is-empty', cart.item_count === 0);
      });
    } catch (e) { /* count refreshes on next page load */ }
  };

  /* ---------- Product form ---------- */
  document.querySelectorAll('[data-product-section]').forEach((section) => {
    const form = section.querySelector('[data-product-form]');
    const json = section.querySelector('[data-product-json]');
    if (!form) return;

    const variants = json ? JSON.parse(json.textContent) : [];
    const idInput = form.querySelector('[data-variant-id]');
    const addButton = form.querySelector('[data-add-button]');
    const addText = form.querySelector('[data-add-text]');
    const errorEl = form.querySelector('[data-form-error]');
    const priceWrap = section.querySelector('[data-price-wrap]');
    const fieldsets = [...form.querySelectorAll('[data-option-index]')];
    const money = (cents) => {
      try {
        const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
        return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100);
      } catch (e) { return (cents / 100).toFixed(2); }
    };

    const selectedOptions = () => fieldsets.map((fs) => fs.querySelector('input:checked')?.value);

    const markUnavailable = () => {
      // Strike out values that have no available variant given the other selections.
      const current = selectedOptions();
      fieldsets.forEach((fs, index) => {
        fs.querySelectorAll('input').forEach((input) => {
          const test = [...current];
          test[index] = input.value;
          const match = variants.find((v) => v.options.every((opt, i) => opt === test[i]));
          input.nextElementSibling.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
    };

    const render = (variant) => {
      if (!variant) {
        addButton.disabled = true;
        addText.textContent = strings.unavailable;
        return;
      }
      idInput.value = variant.id;
      addButton.disabled = !variant.available;
      addText.textContent = variant.available ? strings.addToCart : strings.soldOut;

      if (priceWrap) {
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        priceWrap.innerHTML = `<div class="price${onSale ? ' price--sale' : ''}">
          <span class="price__current">${money(variant.price)}</span>
          ${onSale ? `<s class="price__compare">${money(variant.compare_at_price)}</s>` : ''}
        </div>`;
      }

      if (variant.featured_media) {
        const target = section.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
        const gallery = section.querySelector('.product__gallery');
        if (target && gallery && target !== gallery.firstElementChild) {
          gallery.querySelector('.product__media--hero')?.classList.remove('product__media--hero');
          target.classList.add('product__media--hero');
          gallery.prepend(target);
        }
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };

    fieldsets.forEach((fs) => {
      fs.addEventListener('change', (event) => {
        const valueEl = fs.querySelector('[data-option-value]');
        if (valueEl) valueEl.textContent = event.target.value;
        const options = selectedOptions();
        render(variants.find((v) => v.options.every((opt, i) => opt === options[i])));
        markUnavailable();
      });
    });
    if (fieldsets.length) markUnavailable();

    form.querySelectorAll('[data-qty]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const input = form.querySelector('.qty__input');
        const next = Math.max(1, (parseInt(input.value, 10) || 1) + parseInt(btn.dataset.qty, 10));
        input.value = next;
      });
    });

    form.addEventListener('submit', async (event) => {
      // Dynamic checkout buttons submit their own way; only intercept "Add to bag".
      if (event.submitter && event.submitter !== addButton) return;
      event.preventDefault();
      errorEl.hidden = true;
      addButton.setAttribute('aria-busy', 'true');
      addButton.disabled = true;
      try {
        const res = await fetch(`${routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message);
        showToast(strings.added);
        updateCartCount();
      } catch (err) {
        errorEl.textContent = err.message || strings.unavailable;
        errorEl.hidden = false;
      } finally {
        addButton.removeAttribute('aria-busy');
        addButton.disabled = false;
      }
    });
  });

  /* ---------- Product recommendations ---------- */
  document.querySelectorAll('[data-recommendations]').forEach(async (el) => {
    if (el.querySelector('.grid')) return;
    try {
      const res = await fetch(el.dataset.url);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-recommendations]');
      if (fresh && fresh.innerHTML.trim()) el.innerHTML = fresh.innerHTML;
    } catch (e) { /* recommendations are optional */ }
  });
})();
